import './TableOfContentsSelector.scss';

import { TableOfContentsSelectorProps, ViewType } from '@deckManagement/types/TableOfContentsSelector.types';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { capitalize, forEach, isEmpty, isNil } from 'lodash';
import { ReactNode, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Table of Contents Selector component for the side panel
 * @param deckId - The unique deck identifier that the User is currently viewing
 * @param tocPageItems - The list of pages that are displayed in the Table of Contents
 * @param viewType - The current view type the User is on
 * @param tocPageGroupLookup - Optional lookup of page status groups that are displayed in the Table of Contents in Page View
 * @constructor
 */
const TableOfContentsSelector = ({
  deckId,
  tocPageItems,
  viewType,
  tocPageGroupLookup,
}: TableOfContentsSelectorProps) => {
  const navigate = useNavigate();
  const { pageName } = useParams();

  /**
   * Get the current page name from tocPageItems based on URL pageId, to render it within the selector for the Page View.
   */
  const currentPageName = useMemo(() => {
    if (viewType === ViewType.PAGE_VIEW && pageName && tocPageItems) {
      const currentPage = tocPageItems.find((page) => page.pageName === pageName);
      return currentPage ? `${currentPage.pageName}` : undefined;
    }
    return undefined;
  }, [viewType, pageName, tocPageItems]);

  /**
   * Handle page selection navigation from the side panel Table Selector dropdown
   * @param event - The change event, which will include the unique page identifier selected to navigate to
   */
  const handlePageSelect = (event: SelectChangeEvent) => {
    const selectedPageName = event.target.value;

    if (selectedPageName === 'page-library-home') {
      // Navigate to the Table of Contents home page
      navigate(`/deck-preparation/decks/${deckId}/toc`);
    } else if (deckId && selectedPageName) {
      // Navigate to the selected Page
      navigate(`/deck-preparation/decks/${deckId}/pages/${encodeURIComponent(selectedPageName)}`);
    }
  };

  const generateTocPageMenuItems = () => {
    // Grouped TOC item menu generation, when tocPageGroupLookup props is provided
    if (tocPageGroupLookup) {
      const pageMenuItems: ReactNode[] = [];
      forEach(tocPageGroupLookup, (groupPageItems, statusGroupKey) => {
        // Add group divider
        pageMenuItems.push(
          <Divider key={`group-divider-${statusGroupKey}`} className='toc-page-group-divider'>
            {capitalize(statusGroupKey)}
          </Divider>,
        );

        // Add page group items (sorted alphabetically by pageName)
        const groupPages = [...groupPageItems].map((page) => tocPageItems.find((p) => p.pageId === page));
        const sortedGroupPages = groupPages
          .filter((page) => page !== undefined)
          .sort((a, b) => a.pageName.localeCompare(b.pageName));
        sortedGroupPages.forEach((pageEntry) => {
          pageMenuItems.push(
            <MenuItem
              key={`pg-${pageEntry.pageId}-${pageEntry.serialNo}`}
              value={pageEntry.pageName}
              className='toc-page-select-item'
            >
              {pageEntry.pageName}
            </MenuItem>,
          );
        });
      });

      return pageMenuItems;
    }

    // Standard Table of Contents Menu generation when no tocPageGroupLookup is provided
    return tocPageItems.map((page, idx) => (
      <MenuItem
        key={`pg-${page.pageId}-${page.serialNo}-${idx}`}
        value={page.pageName}
        className='toc-page-select-item'
      >
        {page.pageName}
      </MenuItem>
    ));
  };

  // For conditional rendering based on whether any TOC page items actually exist for the deck
  const hasTocPageItems = !isNil(tocPageItems) && !isEmpty(tocPageItems);
  return (
    <Box className='toc-page-selection-wrapper'>
      <Box className='toc-page-selector'>
        <FormControl variant='filled' className='toc-page-selector-control' fullWidth>
          <InputLabel className='toc-page-selector-label'>Page Library</InputLabel>
          <Select
            labelId='toc-page-selector-label'
            id='toc-page-select'
            value={currentPageName || ''}
            onChange={handlePageSelect}
          >
            {viewType &&
              viewType === ViewType.PAGE_VIEW && [
                <MenuItem key='page-library-home-0' value='page-library-home' className='page-library-home'>
                  ← Page Library
                </MenuItem>,
              ]}
            {!hasTocPageItems && (
              <MenuItem className='toc-page-no-items' disabled>
                No table of content entries.
              </MenuItem>
            )}
            {hasTocPageItems && generateTocPageMenuItems()}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default TableOfContentsSelector;
